import BaseScormAPI from '@/lib/ScormApi/BaseScormAPI'
import {
  LOG_LEVEL_INFO,
  LOG_LEVEL_WARNING,
  SCORM_FALSE,
  SCORM_TRUE,
  STATE_INITIALIZED,
  STATE_TERMINATED
} from './contants'
import CMI from '@/lib/ScormApi/Scorm2004/CMI'
import ADL from '@/lib/ScormApi/Scorm2004/ADL'
import CommentsFromLearnerObject from '@/lib/ScormApi/Scorm2004/CommentsFromLearnerObject'
import CommentsFromLMSObject from '@/lib/ScormApi/Scorm2004/CommentsFromLMSObject'
import ObjectivesObject from '@/lib/ScormApi/Scorm2004/ObjectivesObject'
import InteractionsCorrectResponsesObject from '@/lib/ScormApi/Scorm2004/InteractionsCorrectResponsesObject'
import InteractionsObjectivesObject from '@/lib/ScormApi/Scorm2004/InteractionsObjectivesObject'
import InteractionsObject from '@/lib/ScormApi/Scorm2004/InteractionsObject'
import CMIInterface from "@/lib/ScormApi/Scorm2004/interfaces/CMIInterface"
import ADLInterface from "@/lib/ScormApi/Scorm2004/interfaces/ADLInterface"

export default class ScormAPI2004 extends BaseScormAPI {
  public cmi: CMIInterface
  public adl: ADLInterface

  constructor () {
    super()
    this.cmi = CMI(this)
    this.adl = ADL(this)
  }

  /**
   * @returns {string} bool
   */
  public Initialize () {
    let returnValue = SCORM_FALSE

    if (this.isInitialized()) {
      this.throwSCORMError(103)
    } else if (this.isTerminated()) {
      this.throwSCORMError(104)
    } else {
      this.currentState = STATE_INITIALIZED
      this.lastErrorCode = 0
      returnValue = SCORM_TRUE
      this.processListeners('Initialize')
    }

    this.apiLog('Initialize', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  public Terminate () {
    let returnValue = SCORM_FALSE

    if (this.isNotInitialized()) {
      this.throwSCORMError(112)
    } else if (this.isTerminated()) {
      this.throwSCORMError(113)
    } else {
      this.currentState = STATE_TERMINATED
      this.lastErrorCode = 0
      returnValue = SCORM_TRUE
      this.processListeners('Terminate')
    }

    this.apiLog('Terminate', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  public GetValue (CMIElement: string) {
    let returnValue = ''

    if (this.isNotInitialized()) {
      this.throwSCORMError(122)
    } else if (this.isTerminated()) {
      this.throwSCORMError(123)
    } else {
      this.lastErrorCode = 0
      returnValue = this.getCMIValue(CMIElement)
      this.processListeners('GetValue', CMIElement, returnValue)
    }

    this.apiLog('GetValue', CMIElement, ': returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  public SetValue (CMIElement: string, value: string) {
    let returnValue = ''

    if (this.isNotInitialized()) {
      this.throwSCORMError(132)
    } else if (this.isTerminated()) {
      this.throwSCORMError(133)
    } else {
      this.lastErrorCode = 0
      returnValue = this.setCMIValue(CMIElement, value)
      this.processListeners('SetValue', CMIElement, value)
    }

    this.apiLog('SetValue', CMIElement, ': ' + value + ': result: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)
    return returnValue
  }

  public Commit () {
    let returnValue = SCORM_FALSE

    if (this.isNotInitialized()) {
      this.throwSCORMError(142)
    } else if (this.isTerminated()) {
      this.throwSCORMError(143)
    } else {
      this.lastErrorCode = 0
      returnValue = SCORM_TRUE
      this.processListeners('Commit')
    }

    this.apiLog('Commit', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)
    this.clearSCORMError(returnValue)

    return returnValue
  }

  public GetLastError () {
    const returnValue = String(this.lastErrorCode)

    this.processListeners('GetLastError')

    this.apiLog('GetLastError', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)

    return returnValue
  }

  public GetErrorString (CMIErrorCode: string) {
    let returnValue = ''

    if (CMIErrorCode !== null && CMIErrorCode !== '') {
      returnValue = this.getLmsErrorMessageDetails(+CMIErrorCode)
      this.processListeners('GetErrorString')
    }

    this.apiLog('GetErrorString', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)

    return returnValue
  }

  public GetDiagnostic (CMIErrorCode: string) {
    let returnValue = ''

    if (CMIErrorCode !== null && CMIErrorCode !== '') {
      returnValue = this.getLmsErrorMessageDetails(+CMIErrorCode, true)
      this.processListeners('GetDiagnostic')
    }

    this.apiLog('GetDiagnostic', null, 'returned: ' + returnValue, LOG_LEVEL_INFO)

    return returnValue
  }

  public loadFromJSON (json: any, CMIElement: string) {
    if (!this.isNotInitialized()) {
      console.error('loadFromJSON can only be called before the call to Initialize.')
      return
    }

    CMIElement = CMIElement || 'cmi'

    for (const key in json) {
      if (json.hasOwnProperty(key) && json[key]) {
        const currentCMIElement = CMIElement + '.' + key
        const value = json[key]

        if (value.childArray) {
          for (let i = 0; i < value.childArray.length; i++) {
            this.loadFromJSON(value.childArray[i], currentCMIElement + '.' + i)
          }
        } else if (value.constructor === Object) {
          this.loadFromJSON(value, currentCMIElement)
        } else {
          this.setCMIValue(currentCMIElement, value)
        }
      }
    }
  }

  protected getLmsErrorMessageDetails (errorNumber: number, detail = false) {
    let basicMessage = ''
    let detailMessage = ''

    switch (+errorNumber) {
      case 0:
        basicMessage = 'No Error'
        detailMessage = 'No error occurred, the previous API call was successful.'
        break

      case 101:
        basicMessage = 'General Exception'
        detailMessage = 'No specific error code exists to describe the error. Use GetDiagnostic for more information.'
        break

      case 102:
        basicMessage = 'General Initialization Failure'
        detailMessage = 'Call to Initialize failed for an unknown reason.'
        break

      case 103:
        basicMessage = 'Already Initialized'
        detailMessage = 'Call to Initialize failed because Initialize was already called.'
        break

      case 104:
        basicMessage = 'Content Instance Terminated'
        detailMessage = 'Call to Initialize failed because Terminate was already called.'
        break

      case 111:
        basicMessage = 'General Termination Failure'
        detailMessage = 'Call to Terminate failed for an unknown reason.'
        break

      case 112:
        basicMessage = 'Termination Before Initialization'
        detailMessage = 'Call to Terminate failed because it was made before the call to Initialize.'
        break

      case 113:
        basicMessage = 'Termination After Termination'
        detailMessage = 'Call to Terminate failed because Terminate was already called.'
        break

      case 122:
        basicMessage = 'Retrieve Data Before Initialization'
        detailMessage = 'Call to GetValue failed because it was made before the call to Initialize.'
        break

      case 123:
        basicMessage = 'Retrieve Data After Termination'
        detailMessage = 'Call to GetValue failed because it was made after the call to Terminate.'
        break

      case 132:
        basicMessage = 'Store Data Before Initialization'
        detailMessage = 'Call to SetValue failed because it was made before the call to Initialize.'
        break

      case 133:
        basicMessage = 'Store Data After Termination'
        detailMessage = 'Call to SetValue failed because it was made after the call to Terminate.'
        break

      case 142:
        basicMessage = 'Commit Before Initialization'
        detailMessage = 'Call to Commit failed because it was made before the call to Initialize.'
        break

      case 143:
        basicMessage = 'Commit After Termination'
        detailMessage = 'Call to Commit failed because it was made after the call to Terminate.'
        break

      case 201:
        basicMessage = 'General Argument Error'
        detailMessage = 'An invalid argument was passed to an API method (usually indicates that Initialize, Commit or Terminate did not receive the expected empty string argument.'
        break

      case 301:
        basicMessage = 'General Get Failure'
        detailMessage = 'Indicates a failed GetValue call where no other specific error code is applicable. Use GetDiagnostic for more information.'
        break

      case 351:
        basicMessage = 'General Set Failure'
        detailMessage = 'Indicates a failed SetValue call where no other specific error code is applicable. Use GetDiagnostic for more information.'
        break

      case 391:
        basicMessage = 'General Commit Failure'
        detailMessage = 'Indicates a failed Commit call where no other specific error code is applicable. Use GetDiagnostic for more information.'
        break

      case 401:
        basicMessage = 'Undefined Data Model Element'
        detailMessage = 'The data model element name passed to GetValue or SetValue is not a valid SCORM data model element.'
        break

      case 402:
        basicMessage = 'Unimplemented Data Model Element'
        detailMessage = 'The data model element indicated in a call to GetValue or SetValue is valid, but was not implemented by this LMS. In SCORM 2004, this error would indicate an LMS that is not fully SCORM conformant.'
        break

      case 403:
        basicMessage = 'Data Model Element Value Not Initialized'
        detailMessage = 'Attempt to read a data model element that has not been initialized by the LMS or through a SetValue call. This error condition is often reached during normal execution of a SCO.'
        break

      case 404:
        basicMessage = 'Data Model Element Is Read Only'
        detailMessage = 'SetValue was called with a data model element that can only be read.'
        break

      case 405:
        basicMessage = 'Data Model Element Is Write Only'
        detailMessage = 'GetValue was called on a data model element that can only be written to.'
        break

      case 406:
        basicMessage = 'Data Model Element Type Mismatch'
        detailMessage = 'SetValue was called with a value that is not consistent with the data format of the supplied data model element.'
        break

      case 407:
        basicMessage = 'Data Model Element Value Out Of Range'
        detailMessage = 'The numeric value supplied to a SetValue call is outside of the numeric range allowed for the supplied data model element.'
        break

      case 408:
        basicMessage = 'Data Model Dependency Not Established'
        detailMessage = 'Some data model elements cannot be set until another data model element was set. This error condition indicates that the prerequisite element was not set before the dependent element.'
        break

      default:
        basicMessage = ''
        detailMessage = ''
        break
    }

    return detail ? detailMessage : basicMessage
  }

  private setCMIValue (CMIElement: string, value: string) {
    if (!CMIElement || CMIElement === '') {
      return SCORM_FALSE
    }

    const structure = CMIElement.split('.')
    let refObject: any = this
    let returnValue = SCORM_FALSE

    for (let i = 0; i < structure.length; i++) {
      const attribute = structure[i]

      if (i === structure.length - 1) {
        if ((attribute.substr(0, 8) == '{target=') && (typeof refObject._isTargetValid === 'function')) {
          this.throwSCORMError(404)
        } else if (!refObject.hasOwnProperty(attribute)) {
          this.throwSCORMError(401, 'The data model element passed to SetValue (' + CMIElement + ') is not a valid SCORM data model element.')
        } else {
          refObject[attribute] = value
          if (this.lastErrorCode === 0) {
            returnValue = SCORM_TRUE
          }
        }
      } else {
        refObject = refObject[attribute]
        if (!refObject) {
          this.throwSCORMError(401, 'The data model element passed to SetValue (' + CMIElement + ') is not a valid SCORM data model element.')
          break
        }

        if (refObject.hasOwnProperty('childArray')) {
          const index = parseInt(structure[i + 1], 10)

          // SCO is trying to set an item on an array
          if (!isNaN(index)) {
            const item = refObject.childArray[index]

            if (item) {
              refObject = item
            } else {
              let newChild

              if (CMIElement.indexOf('cmi.comments_from_learner') > -1) {
                newChild = CommentsFromLearnerObject(this)
              } else if (CMIElement.indexOf('cmi.comments_from_lms') > -1) {
                newChild = CommentsFromLMSObject(this)
              } else if (CMIElement.indexOf('cmi.objectives') > -1) {
                newChild = ObjectivesObject(this)
              } else if (CMIElement.indexOf('.correct_responses') > -1) {
                newChild = InteractionsCorrectResponsesObject(this)
              } else if (CMIElement.indexOf('.objectives') > -1) {
                newChild = InteractionsObjectivesObject(this)
              } else if (CMIElement.indexOf('cmi.interactions') > -1) {
                newChild = InteractionsObject(this)
              }

              if (!newChild) {
                this.throwSCORMError(401, 'The data model element passed to SetValue (' + CMIElement + ') is not a valid SCORM data model element.')
              } else {
                refObject.childArray.push(newChild)

                refObject = newChild
              }
            }

            // Have to update i value to skip the array position
            i++
          }
        }
      }
    }

    if (returnValue === SCORM_FALSE) {
      this.apiLog('SetValue', null, 'There was an error setting the value for: ' + CMIElement + ', value of: ' + value, LOG_LEVEL_WARNING)
    }

    return returnValue
  }

  private getCMIValue (CMIElement: string) {
    if (!CMIElement || CMIElement === '') {
      return ''
    }

    const structure = CMIElement.split('.')
    let refObject: any = this

    for (let i = 0; i < structure.length; i++) {
      const attribute = structure[i]
      if ((attribute.substr(0, 8) == '{target=') && (typeof refObject._isTargetValid === 'function')) {
        const target = attribute.substr(8, attribute.length - 9)
        return refObject._isTargetValid(target)
      } else if (refObject.hasOwnProperty('childArray') && refObject.childArray.hasOwnProperty(attribute)) {
        refObject = refObject.childArray[attribute]
      } else if (!refObject.hasOwnProperty(attribute)) {
        this.throwSCORMError(401, `The data model element passed to GetValue (${ CMIElement }, ${ attribute }) is not a valid SCORM data model element.`)
        return ''
      } else {
        refObject = refObject[attribute]
      }
    }

    return refObject || ''
  }
}
